/**
 * 请求拦截器工具
 * 拦截 fetch 和 XMLHttpRequest 请求
 */

import type { RequestMethod, RequestType, RequestStatus } from '../types/network'

/**
 * 待处理请求信息
 */
export interface PendingRequest {
  /** 请求 URL */
  url: string
  /** 请求方法 */
  method: RequestMethod
  /** 请求类型 */
  type: RequestType
  /** 请求头 */
  headers?: Record<string, string>
  /** 请求体 */
  body?: string
}

/**
 * 响应信息
 */
export interface ResponseInfo {
  /** 响应状态码 */
  status?: number
  /** 响应状态文本 */
  statusText?: string
  /** 响应头 */
  responseHeaders?: Record<string, string>
  /** 响应体 */
  responseBody?: string
  /** 请求状态 */
  requestStatus: RequestStatus
  /** 错误信息 */
  errorMessage?: string
  /** 请求结束时间戳 */
  endTime: number
}

/**
 * 拦截器回调函数
 */
export interface InterceptorCallbacks {
  /** 请求开始回调，返回请求 ID */
  onRequestStart: (request: PendingRequest) => string
  /** 请求结束回调 */
  onRequestEnd: (requestId: string, response: ResponseInfo) => void
}

/**
 * 拦截器配置
 */
export interface InterceptorConfig {
  /** 是否记录请求头 */
  recordHeaders?: boolean
  /** 是否记录请求体 */
  recordBody?: boolean
  /** 是否记录响应体 */
  recordResponse?: boolean
  /** 请求体最大记录大小 */
  maxBodySize?: number
  /** 响应体最大记录大小 */
  maxResponseSize?: number
  /** 排除的 URL 正则（支持字符串或 RegExp 对象） */
  excludeUrls?: (string | RegExp)[]
}

// 标记已拦截的 Symbol
const FETCH_INTERCEPTED = Symbol('xt-tracker-fetch-intercepted')
const XHR_INTERCEPTED = Symbol('xt-tracker-xhr-intercepted')

/**
 * 截断字符串
 */
function truncateString(str: string, maxSize: number): string {
  if (!str || str.length <= maxSize) return str
  return str.slice(0, maxSize) + `...[TRUNCATED: ${str.length} bytes]`
}

/**
 * 判断是否应该监控该 URL
 */
export function shouldMonitorUrl(url: string, excludeUrls?: (string | RegExp)[]): boolean {
  
  // 排除静态资源（取 ? 前的部分进行匹配）
  const urlPath = url.split('?')[0]
  const staticExts = /\.(js|css|png|jpg|jpeg|gif|svg|ico|webp|woff|woff2|ttf|eot|otf|mp4|webm|mp3|wav|cmf|m3u8|flv|ts|hls)$/i
  if (staticExts.test(urlPath)) return false

  // 排除 data: 和 blob: URL
  if (url.startsWith('data:') || url.startsWith('blob:')) return false

  // 检查自定义排除规则
  if (excludeUrls?.length) {
    for (const pattern of excludeUrls) {
      // 如果是字符串，转换为 RegExp；如果是 RegExp 对象，直接使用
      const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern
      if (regex.test(url)) return false
    }
  }

  return true
}

/**
 * 规范化请求方法
 */
function normalizeMethod(method: string): RequestMethod {
  const upper = method.toUpperCase()
  const validMethods: RequestMethod[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS']
  return validMethods.includes(upper as RequestMethod) ? upper as RequestMethod : 'GET'
}

/**
 * 从 Headers 对象提取键值对
 */
function extractHeaders(headers: Headers): Record<string, string> {
  const result: Record<string, string> = {}
  headers.forEach((value, key) => {
    result[key] = value
  })
  return result
}

/**
 * 解析请求体
 */
async function parseBody(body: BodyInit | null | undefined, maxSize: number): Promise<string | undefined> {
  if (!body) return undefined

  try {
    if (typeof body === 'string') {
      return truncateString(body, maxSize)
    }
    if (body instanceof FormData) {
      const entries: string[] = []
      body.forEach((value, key) => {
        entries.push(`${key}=${typeof value === 'string' ? value : '[File]'}`)
      })
      return truncateString(entries.join('&'), maxSize)
    }
    if (body instanceof URLSearchParams) {
      return truncateString(body.toString(), maxSize)
    }
    if (body instanceof ArrayBuffer || body instanceof Blob) {
      const size = body instanceof ArrayBuffer ? body.byteLength : body.size
      return `[Binary: ${size} bytes]`
    }
    // 其他情况尝试 JSON 序列化
    return truncateString(JSON.stringify(body), maxSize)
  } catch {
    return '[Unable to parse body]'
  }
}

/**
 * 读取 Fetch 响应体
 */
async function readFetchResponse(response: Response, maxSize: number): Promise<string | undefined> {
  try {
    const cloned = response.clone()
    const text = await cloned.text()
    return truncateString(text, maxSize)
  } catch {
    return '[Unable to read response]'
  }
}

/**
 * 拦截 fetch
 */
export function interceptFetch(
  callbacks: InterceptorCallbacks,
  config: InterceptorConfig = {}
): () => void {
  if (typeof window === 'undefined' || !window.fetch) {
    return () => {}
  }

  // 检查是否已被拦截
  const originalFetch = window.fetch
  if ((originalFetch as any)[FETCH_INTERCEPTED]) {
    return () => {}
  }

  const {
    recordHeaders = false,
    recordBody = true,
    recordResponse = true,
    maxBodySize = 10240,
    maxResponseSize = 10240,
    excludeUrls = [],
  } = config

  // 创建代理 fetch
  const proxiedFetch = async function(
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    // 解析请求信息
    let url: string = ''
    let method: RequestMethod = 'GET'
    let headers: Record<string, string> | undefined
    let bodyStr: string | undefined

    try {
      if (input instanceof Request) {
        url = input.url
        method = normalizeMethod(input.method)
        if (recordHeaders && input.headers) {
          headers = extractHeaders(input.headers)
        }
        if (recordBody && input.body) {
          // Request body 只能读取一次，这里我们需要克隆
          const clonedRequest = input.clone()
          bodyStr = await parseBody(await clonedRequest.arrayBuffer().then(b => new TextDecoder().decode(b)).catch(() => ''), maxBodySize)
        }
      } else {
        url = input instanceof URL ? input.href : input
        method = normalizeMethod(init?.method || 'GET')
        if (recordHeaders && init?.headers) {
          const headersObj = new Headers(init.headers as HeadersInit)
          headers = extractHeaders(headersObj)
        }
        if (recordBody && init?.body) {
          bodyStr = await parseBody(init.body, maxBodySize)
        }
      }

      // 检查是否应该监控
      if (!shouldMonitorUrl(url, excludeUrls)) {
        return originalFetch.call(window, input, init)
      }

      // 记录请求开始
      const requestId = callbacks.onRequestStart({
        url,
        method,
        type: 'fetch',
        headers,
        body: bodyStr,
      })

      // 发送请求
      const response = await originalFetch.call(window, input, init)

      // 读取响应信息
      const responseHeaders: Record<string, string> = {}
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value
      })

      const responseBody = recordResponse
        ? await readFetchResponse(response, maxResponseSize)
        : undefined

      // 记录请求结束
      callbacks.onRequestEnd(requestId, {
        status: response.status,
        statusText: response.statusText,
        responseHeaders,
        responseBody,
        requestStatus: response.ok ? 'success' : 'error',
        endTime: Date.now(),
      })

      return response
    } catch (error) {
      // 网络错误或请求失败
      if (url && shouldMonitorUrl(url, excludeUrls)) {
        // 尝试获取之前记录的请求 ID（这里简化处理，直接创建新记录）
        const requestId = callbacks.onRequestStart({
          url: url || '',
          method: method || 'GET',
          type: 'fetch',
          headers,
          body: bodyStr,
        })

        callbacks.onRequestEnd(requestId, {
          requestStatus: 'error',
          errorMessage: error instanceof Error ? error.message : 'Network error',
          endTime: Date.now(),
        })
      }

      throw error
    }
  }

  // 标记已拦截
  ;(proxiedFetch as any)[FETCH_INTERCEPTED] = true

  // 替换全局 fetch
  window.fetch = proxiedFetch

  // 返回销毁函数
  return () => {
    window.fetch = originalFetch
  }
}

/**
 * 拦截 XMLHttpRequest
 * 使用代理模式实现，确保跨浏览器兼容性
 */
export function interceptXHR(
  callbacks: InterceptorCallbacks,
  config: InterceptorConfig = {}
): () => void {
  if (typeof window === 'undefined' || !window.XMLHttpRequest) {
    return () => {}
  }

  // 检查是否已被拦截
  const OriginalXHR = window.XMLHttpRequest
  if ((OriginalXHR as any)[XHR_INTERCEPTED]) {
    return () => {}
  }

  const {
    recordHeaders = false,
    recordBody = true,
    recordResponse = true,
    maxBodySize = 10240,
    maxResponseSize = 10240,
    excludeUrls = [],
  } = config

  // 需要代理的 XHR 属性（只读属性）
  const XHR_READ_ONLY_PROPS = [
    'readyState', 'response', 'responseText', 'responseXML',
    'responseURL', 'status', 'statusText', 'upload'
  ]

  // 需要代理的 XHR 属性（可读写属性）
  const XHR_READWRITE_PROPS = [
    'responseType', 'timeout', 'withCredentials'
  ]

  // 需要代理的事件处理器属性
  const XHR_EVENT_HANDLERS = [
    'onabort', 'onerror', 'onload', 'onloadend', 'onloadstart',
    'onprogress', 'onreadystatechange', 'ontimeout'
  ]

  // 解析请求体
  function parseXHRBody(body: Document | XMLHttpRequestBodyInit | null, maxSize: number): string {
    try {
      if (!body) return ''
      if (typeof body === 'string') {
        return truncateString(body, maxSize)
      }
      if (body instanceof FormData) {
        const entries: string[] = []
        body.forEach((value, key) => {
          entries.push(`${key}=${typeof value === 'string' ? value : '[File]'}`)
        })
        return truncateString(entries.join('&'), maxSize)
      }
      if (body instanceof URLSearchParams) {
        return truncateString(body.toString(), maxSize)
      }
      if (body instanceof ArrayBuffer || body instanceof Blob) {
        const size = body instanceof ArrayBuffer ? body.byteLength : body.size
        return `[Binary: ${size} bytes]`
      }
      if (body instanceof Document) {
        return truncateString(body.documentElement.outerHTML, maxSize)
      }
      return JSON.stringify(body)
    } catch {
      return '[Unable to parse body]'
    }
  }

  // 创建代理 XHR 构造函数
  function ProxiedXMLHttpRequest(this: any): void {
    // 创建真实的 XHR 实例
    const realXHR = new OriginalXHR()

    // 存储拦截器状态
    let requestId: string | null = null
    let method: RequestMethod = 'GET'
    let url: string = ''
    let requestHeaders: Record<string, string> = {}
    let requestBody: string | undefined
    let isMonitored = false
    let eventListenersSetup = false

    // 设置事件监听器（用于请求监控）
    const setupMonitoringListeners = () => {
      if (eventListenersSetup) return
      eventListenersSetup = true

      const handleEnd = (requestStatus: RequestStatus, errorMessage?: string) => {
        if (!requestId) return

        const responseHeaders: Record<string, string> = {}
        try {
          const headerStr = realXHR.getAllResponseHeaders()
          if (headerStr) {
            headerStr.split('\r\n').forEach(line => {
              const colonIndex = line.indexOf(': ')
              if (colonIndex > 0) {
                const key = line.slice(0, colonIndex)
                const value = line.slice(colonIndex + 2)
                if (key && value) {
                  responseHeaders[key] = value
                }
              }
            })
          }
        } catch {
          // 忽略错误
        }

        let responseBody: string | undefined
        if (recordResponse) {
          try {
            const response = realXHR.responseText || realXHR.response
            if (typeof response === 'string') {
              responseBody = truncateString(response, maxResponseSize)
            } else if (response) {
              responseBody = JSON.stringify(response)
            }
          } catch {
            responseBody = '[Unable to read response]'
          }
        }

        callbacks.onRequestEnd(requestId, {
          status: realXHR.status,
          statusText: realXHR.statusText,
          responseHeaders,
          responseBody,
          requestStatus,
          errorMessage,
          endTime: Date.now(),
        })

        requestId = null
      }

      // 使用 addEventListener 添加监控事件
      realXHR.addEventListener('load', () => {
        if (!isMonitored) return
        const status = realXHR.status
        handleEnd(status >= 200 && status < 400 ? 'success' : 'error')
      })

      realXHR.addEventListener('error', () => {
        if (!isMonitored) return
        handleEnd('error', 'Network error')
      })

      realXHR.addEventListener('timeout', () => {
        if (!isMonitored) return
        handleEnd('timeout', 'Request timeout')
      })

      realXHR.addEventListener('abort', () => {
        if (!isMonitored) return
        handleEnd('abort', 'Request aborted')
      })
    }

    // 代理只读属性
    XHR_READ_ONLY_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: function() {
          return (realXHR as any)[prop]
        },
        enumerable: true,
        configurable: true
      })
    })

    // 代理可读写属性
    XHR_READWRITE_PROPS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: function() {
          return (realXHR as any)[prop]
        },
        set: function(value) {
          (realXHR as any)[prop] = value
        },
        enumerable: true,
        configurable: true
      })
    })

    // 代理事件处理器属性
    XHR_EVENT_HANDLERS.forEach(prop => {
      Object.defineProperty(this, prop, {
        get: function() {
          return (realXHR as any)[prop]
        },
        set: function(value) {
          (realXHR as any)[prop] = value
        },
        enumerable: true,
        configurable: true
      })
    })

    // 代理 open 方法
    this.open = function(methodArg: string, urlArg: string | URL, async?: boolean, user?: string | null, password?: string | null): void {
      method = normalizeMethod(methodArg)
      url = urlArg instanceof URL ? urlArg.href : urlArg
      requestHeaders = {}
      requestBody = undefined
      requestId = null
      isMonitored = false

      // 调用真实的 open
      if (arguments.length >= 5) {
        realXHR.open(methodArg, urlArg, async !== false, user ?? null, password ?? null)
      } else if (arguments.length >= 4) {
        realXHR.open(methodArg, urlArg, async !== false, user ?? null)
      } else if (arguments.length >= 3) {
        realXHR.open(methodArg, urlArg, async !== false)
      } else {
        realXHR.open(methodArg, urlArg)
      }
    }

    // 代理 setRequestHeader 方法
    this.setRequestHeader = function(name: string, value: string): void {
      if (recordHeaders) {
        requestHeaders[name] = value
      }
      realXHR.setRequestHeader(name, value)
    }

    // 代理 send 方法
    this.send = function(body?: Document | XMLHttpRequestBodyInit | null): void {
      // 检查是否应该监控
      isMonitored = shouldMonitorUrl(url, excludeUrls)

      if (isMonitored) {
        // 设置监控事件监听器
        setupMonitoringListeners()

        // 解析请求体
        if (recordBody && body) {
          requestBody = parseXHRBody(body, maxBodySize)
        }

        // 记录请求开始
        requestId = callbacks.onRequestStart({
          url,
          method,
          type: 'xhr',
          headers: recordHeaders ? { ...requestHeaders } : undefined,
          body: requestBody,
        })
      }

      // 发送请求
      realXHR.send(body)
    }

    // 代理 abort 方法
    this.abort = function(): void {
      if (requestId) {
        callbacks.onRequestEnd(requestId, {
          requestStatus: 'abort',
          errorMessage: 'Request aborted',
          endTime: Date.now(),
        })
        requestId = null
      }
      realXHR.abort()
    }

    // 代理其他方法
    this.getAllResponseHeaders = function(): string {
      return realXHR.getAllResponseHeaders()
    }

    this.getResponseHeader = function(name: string): string | null {
      return realXHR.getResponseHeader(name)
    }

    this.overrideMimeType = function(mime: string): void {
      realXHR.overrideMimeType(mime)
    }

    // 代理事件监听方法
    this.addEventListener = function<K extends keyof XMLHttpRequestEventMap>(
      type: K,
      listener: (this: XMLHttpRequest, ev: XMLHttpRequestEventMap[K]) => any,
      options?: boolean | AddEventListenerOptions
    ): void {
      realXHR.addEventListener(type, listener as EventListener, options)
    }

    this.removeEventListener = function<K extends keyof XMLHttpRequestEventMap>(
      type: K,
      listener: (this: XMLHttpRequest, ev: XMLHttpRequestEventMap[K]) => any,
      options?: boolean | EventListenerOptions
    ): void {
      realXHR.removeEventListener(type, listener as EventListener, options)
    }

    this.dispatchEvent = function(event: Event): boolean {
      return realXHR.dispatchEvent(event)
    }
  }

  // 设置原型链，确保 instanceof 检查正常工作
  ProxiedXMLHttpRequest.prototype = Object.create(OriginalXHR.prototype)
  ProxiedXMLHttpRequest.prototype.constructor = ProxiedXMLHttpRequest

  // 复制静态常量
  ;(ProxiedXMLHttpRequest as any).UNSENT = 0
  ;(ProxiedXMLHttpRequest as any).OPENED = 1
  ;(ProxiedXMLHttpRequest as any).HEADERS_RECEIVED = 2
  ;(ProxiedXMLHttpRequest as any).LOADING = 3
  ;(ProxiedXMLHttpRequest as any).DONE = 4

  // 标记已拦截
  ;(ProxiedXMLHttpRequest as any)[XHR_INTERCEPTED] = true

  // 替换全局 XMLHttpRequest
  ;(window as any).XMLHttpRequest = ProxiedXMLHttpRequest

  // 返回销毁函数
  return () => {
    window.XMLHttpRequest = OriginalXHR
  }
}


/**
 * 生成请求 ID
 */
export function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}
